type BinanceKline = [
  number, // openTime
  string, // open
  string, // high
  string, // low
  string, // close
  string, // volume
  number, // closeTime
  string,
  number,
  string,
  string,
  string
];

function extractDecimals(priceStr: string) {
  const dot = priceStr.indexOf('.');
  if (dot === -1) {
    return '00';
  }
  const dec = priceStr.slice(dot + 1);
  // "Most significant digits" for the decimal part => take the first 5 digits after the dot.
  // Pad with zeros to keep length stable.
  return dec.slice(0, 2).padEnd(2, '0');
}


function extractSignificantDigits(priceStr: string) {
  const dot = priceStr.indexOf('.');
  if (dot === -1) {
    return '00';
  }
  return priceStr.slice(0, dot);
}

export type SolPriceAnchor = {
  /** Start time used to compute the candle window. */
  sessionStartTimeMs: number;
  /** Open time (ms) of the earliest candle used (inclusive). */
  windowStartCandleOpenTimeMs: number;
  /** Open time (ms) of the latest candle used. */
  windowEndCandleOpenTimeMs: number;
  /** Concatenated decimals-only seed string, built from O/H/L/C for each candle in the window. */
  solPriceSeed: string;
  /** Endpoint used. */
  source: string;
};

export async function fetchSolPriceAnchorLast5Minutes(sessionStartTimeMs: number): Promise<SolPriceAnchor> {
  // Use the last 5 fully closed 1m candles to avoid non-final data.
  // Example: if sessionStartTime is within minute M, use candles [M-5, M-4, M-3, M-2, M-1].
  const endCandleOpenTimeMs = Math.floor(sessionStartTimeMs / 60_000) * 60_000 - 60_000;
  const startCandleOpenTimeMs = endCandleOpenTimeMs - 4 * 60_000;

  const endpoints = [
    'https://api.binance.com/api/v3/klines',
    // Often used as a high-availability mirror for market data:
    'https://data-api.binance.vision/api/v3/klines',
  ];

  const params = new URLSearchParams({
    symbol: 'SOLUSDT',
    interval: '1m',
    startTime: String(startCandleOpenTimeMs),
    endTime: String(endCandleOpenTimeMs + 59_999),
    limit: '5',
  });

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 2500);
  try {
    let lastErr: unknown = null;

    for (const baseUrl of endpoints) {
      try {
        const res = await fetch(`${baseUrl}?${params.toString()}`, { signal: controller.signal });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        const klines = Array.isArray(data) ? (data as BinanceKline[]) : [];
        if (klines.length < 1) {
          throw new Error('Empty kline response');
        }

        // Binance should return candles ordered by openTime ascending.
        const sorted = klines.slice().sort((a, b) => Number(a[0]) - Number(b[0]));
        const windowStart = Number(sorted[0][0]);
        const windowEnd = Number(sorted[sorted.length - 1][0]);

        // Build the seed string from the last 5 minutes:
        // decimals5(open|high|low|close) for each candle in chronological order.
        // If Binance returns fewer candles for some reason, we still produce a deterministic seed.
        let solPriceSeed = extractSignificantDigits(sorted[0][1]);
        for (const k of sorted) {
          solPriceSeed += extractDecimals(String(k[1])); // open
          solPriceSeed += extractDecimals(String(k[2])); // high
          solPriceSeed += extractDecimals(String(k[3])); // low
          solPriceSeed += extractDecimals(String(k[4])); // close
        }

        return {
          sessionStartTimeMs,
          windowStartCandleOpenTimeMs: windowStart,
          windowEndCandleOpenTimeMs: windowEnd,
          solPriceSeed,
          source: baseUrl,
        };
      } catch (e) {
        lastErr = e;
      }
    }

    throw lastErr ?? new Error('Failed to fetch Binance klines');
  } finally {
    clearTimeout(timeout);
  }
}


